// Every timestamp the API returns is an ISO-8601 UTC string (e.g.
// "2026-08-27T15:58:15.895Z") — fine on the wire, unreadable in a table
// cell. formatDateTime renders it in the viewer's own local timezone as
// a plain "YYYY-MM-DD HH:mm:ss", which every table column showing a date
// should go through instead of binding the raw string via data-index.
export function formatDateTime(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

// The agent reports throughput in BYTES per second (it diffs the byte
// counters in /proc/net/dev). Every CDN console states bandwidth in BITS
// per second instead — Cloudflare, EdgeOne and Aliyun all do — so the
// conversion belongs here, once, rather than in each view. Getting this
// wrong understates a link by 8x, so the multiply is not optional.
//
// Network units are decimal by convention (1 Mbps = 10^6 bits), which is
// why this divides by 1000 and not 1024.
export function bitrate(bytesPerSec?: number | null): string {
  const p = bitrateParts(bytesPerSec)
  return `${p.value.toFixed(p.precision)} ${p.unit}`
}

// The same, as a number and its unit apart — for a-statistic, which
// formats any value that is not a number as a date.
export function bitrateParts(bytesPerSec?: number | null): { value: number; unit: string; precision: number } {
  const units = ['bps', 'Kbps', 'Mbps', 'Gbps', 'Tbps']
  let i = 0
  let n = Math.max(0, bytesPerSec ?? 0) * 8
  while (n >= 1000 && i < units.length - 1) {
    n /= 1000
    i++
  }
  // Three significant figures keeps the column width stable: "9.99 Mbps"
  // and "999 Mbps" occupy the same space.
  const precision = i === 0 ? 0 : n < 10 ? 2 : n < 100 ? 1 : 0
  return { value: precision === 0 ? Math.round(n) : n, unit: units[i], precision }
}

// A plain count (concurrent connections, say), shortened once it stops
// being readable at full length.
export function compactCount(v?: number | null): string {
  const n = Math.max(0, v ?? 0)
  if (n < 1000) return String(n)
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`
  return `${(n / 1_000_000).toFixed(1)}M`
}

// A figure with its unit made into an axis label: no space, no trailing
// zeros after the point — "1.5Mbps" beside "2Mbps", not "1.50 Mbps" beside
// "2.00 Mbps".
export function tight(s: string): string {
  return s.replace(/(\.\d*?[1-9])0+(?= )|\.0+(?= )/, '$1').replace(' ', '')
}

// compactCount for an axis label, where "1k" beside "1.5k" reads better
// than "1.0k".
export function axisCount(v?: number | null): string {
  return compactCount(v).replace(/\.0(?=[kM]$)/, '')
}

// A per-second rate. The unit is part of the value: "4" and "4/s" mean
// very different things when the number is a drop count.
export function perSecond(v?: number | null): string {
  return `${compactCount(v)}/s`
}

// A packet rate. Kept separate from bitrate() on purpose: packets/s and
// bits/s are different measurements of the same traffic, and an edge node
// runs out of packet budget long before it runs out of bandwidth — a flood
// of 64-byte packets pins the kernel's per-packet path while the link looks
// half idle. Showing only one of the two hides that.
export function packetRate(v?: number | null): string {
  return `${compactCount(v)} pps`
}

// Traffic volume, in binary units the way traffic is sold (1 GB = 1024
// MB), matching what the control plane counts a package's quota in.
export function volume(bytes?: number | null): string {
  const p = volumeParts(bytes)
  return `${p.value.toFixed(p.precision)} ${p.unit}`
}

// The same, as a number and its unit apart.
export function volumeParts(bytes?: number | null): { value: number; unit: string; precision: number } {
  let n = Math.max(0, bytes ?? 0)
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let i = 0
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024
    i++
  }
  const precision = i === 0 ? 0 : n < 10 ? 2 : n < 100 ? 1 : 0
  return { value: precision === 0 ? Math.round(n) : n, unit: units[i], precision }
}

// A count for a headline figure: whole up to ten thousand, then in the
// reader's own large units — 万 and 亿 in Chinese, K, M and B in English —
// as a number and its unit apart, like bitrateParts.
export function countParts(v?: number | null, zh = true): { value: number; unit: string; precision: number } {
  const n = Math.max(0, Math.round(v ?? 0))
  const steps: Array<[number, string]> = zh
    ? [[1e8, '亿'], [1e4, '万']]
    : [[1e9, 'B'], [1e6, 'M'], [1e3, 'K']]
  for (const [size, unit] of steps) {
    if (n >= size) {
      const x = n / size
      return { value: x, unit, precision: x < 1000 ? 2 : 0 }
    }
  }
  return { value: n, unit: '', precision: 0 }
}

// countParts as one string, trailing zeros dropped: "4.04万", "3万", "8,642".
export function countText(v?: number | null, zh = true): string {
  const p = countParts(v, zh)
  return p.unit ? `${Number(p.value.toFixed(p.precision))}${p.unit}` : p.value.toLocaleString()
}

// An amount in 分 as 元, the way a bill writes it: 123456 → "1,234.56".
// Integer arithmetic throughout, so no float ever rounds a cent away.
export function money(cents?: number | null): string {
  const c = Math.round(cents ?? 0)
  const abs = Math.abs(c)
  const whole = Math.floor(abs / 100).toLocaleString('en-US')
  return `${c < 0 ? '-' : ''}${whole}.${String(abs % 100).padStart(2, '0')}`
}

// A time for a tight table column: this year's without the year,
// "10-26 20:18:56"; another year's in full.
export function stamp(iso?: string | null): string {
  const s = formatDateTime(iso)
  return s.startsWith(`${new Date().getFullYear()}-`) ? s.slice(5) : s
}
